import { Link } from "react-router-dom";
import useSiteContent from "@/hooks/useSiteContent";
import WhatsAppCta from "@/components/WhatsAppCta";

/** Marketing footer — 5-column layout: Company / Services / Support & Help /
 *  Legal / Trust & Technical. Every link maps to a real route or a CMS slug
 *  seeded by seed_marketing.py. Kept in sync with MarketingShell.js.
 */
const FOOTER_COLUMNS = [
    {
        heading: "Company",
        links: [
            { label: "About Us",           to: "/about" },
            { label: "Our Team",           to: "/p/team" },
            { label: "Vision & Mission",   to: "/p/vision-mission" },
            { label: "Made in India",      to: "/p/made-in-india" },
            { label: "Jobs & Opportunities", to: "/p/jobs" },
            { label: "Contact",            to: "/contact" },
        ],
    },
    {
        heading: "Services",
        links: [
            { label: "Doctor Digital Identity", to: "/products#doctor-digital-identity" },
            { label: "AI Profile Creation",     to: "/products#ai-profile-creation" },
            { label: "QR Visiting Card",        to: "/products#qr-visiting-card" },
            { label: "SEO & Google Visibility", to: "/products#seo-google-visibility" },
            { label: "Custom Domain",           to: "/products#custom-domain" },
        ],
    },
    {
        heading: "Support & Help",
        links: [
            { label: "Help Centre & FAQ",       to: "/help" },
            { label: "Raise a Support Ticket",  to: "/help#raise-ticket" },
            { label: "AI Support Assistant",    to: "/p/ai-support-assistant" },
            { label: "Contact Us",              to: "/contact" },
            { label: "Demo Videos",             to: "/demos" },
        ],
        extra: "whatsapp",
    },
    {
        heading: "Legal",
        links: [
            { label: "Privacy Policy",       to: "/p/legal-privacy" },
            { label: "Terms of Service",     to: "/p/legal-terms" },
            { label: "Refund Policy",        to: "/p/legal-refund" },
            { label: "Copyright Policy",     to: "/p/legal-copyright" },
            { label: "Digital Transaction",  to: "/p/legal-digital-transaction" },
            { label: "Customer Consent",     to: "/p/legal-consent" },
            { label: "Legal Disclaimer",     to: "/p/legal-disclaimer" },
        ],
    },
    {
        heading: "Trust & Technical",
        links: [
            { label: "Ethical AI",                to: "/p/tech-ethical-ai" },
            { label: "Data Security",             to: "/p/tech-data-security" },
            { label: "Data Protection & Encryption", to: "/p/tech-data-protection" },
            { label: "Compliance",                to: "/p/tech-compliance" },
            { label: "Bank-grade Security",       to: "/p/tech-security" },
            { label: "Admin Support",             to: "/p/tech-admin-support" },
        ],
    },
];

const FOOTER_DEFAULTS = {
    footer: {
        tagline: "A professional identity and brand platform for practicing doctors. Not a diagnostic or emergency medical service.",
        legal: "",
    },
    contact: {
        support_phone: "+91 7722090290",
        support_email: "support@doctorsprofile.in",
        office_address: "A-803 Palatial Height, Chandivali Farm Rd, Andheri, Mumbai, Maharashtra 400072, India",
    },
};

export default function MarketingFooter() {
    const year = new Date().getFullYear();
    const { content } = useSiteContent(FOOTER_DEFAULTS);
    const f = content.footer || FOOTER_DEFAULTS.footer;
    const c = content.contact || FOOTER_DEFAULTS.contact;
    return (
        <footer className="bg-[#0b2740] text-white/80 mt-auto" data-testid="marketing-footer">
            <div className="max-w-7xl mx-auto px-6 py-14">
                {/* Brand strip + 5 columns */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
                    <div className="col-span-2 lg:col-span-1">
                        <div className="flex items-center gap-2.5 text-white font-bold text-lg tracking-tight">
                            <img src="/brand-icon.png" alt="Doctor Digital ID" className="h-10 w-10 object-contain" />
                            <span>DOCTOR <span className="text-[#60A5FA]">DIGITAL ID</span></span>
                        </div>
                        <p className="text-xs mt-3 max-w-xs text-white/60 leading-relaxed" data-testid="footer-tagline">
                            {f.tagline}
                        </p>
                        <div className="mt-4 text-[11px] text-white/55 leading-relaxed">
                            A product of<br />
                            <span className="text-white/85 font-semibold" data-testid="footer-legal-name">
                                HUMANITYFIRST SOFTWARE TECHNOLOGY PRIVATE LIMITED
                            </span>
                        </div>
                    </div>
                    {FOOTER_COLUMNS.map((col) => (
                        <div
                            key={col.heading}
                            data-testid={`footer-col-${col.heading.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                        >
                            <div className="text-white text-[11px] font-semibold uppercase tracking-wider mb-3">
                                {col.heading}
                            </div>
                            <ul className="space-y-2 text-[13px]">
                                {col.links.map((l) => (
                                    <li key={col.heading + l.label}>
                                        <Link className="text-white/70 hover:text-white transition-colors" to={l.to}>
                                            {l.label}
                                        </Link>
                                    </li>
                                ))}
                                {col.extra === "whatsapp" ? (
                                    <li className="pt-1">
                                        <WhatsAppCta variant="footer" label="WhatsApp us" />
                                    </li>
                                ) : null}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Address + contact */}
                <div className="mt-10 pt-6 border-t border-white/10 grid grid-cols-1 md:grid-cols-3 gap-4 text-[12px] text-white/55">
                    <div data-testid="footer-address" className="leading-relaxed">
                        <div className="text-white/80 font-semibold uppercase text-[10px] tracking-wider mb-1">Registered address</div>
                        {c.office_address}
                    </div>
                    <div className="leading-relaxed">
                        <div className="text-white/80 font-semibold uppercase text-[10px] tracking-wider mb-1">Contact</div>
                        <a href={`tel:${(c.support_phone || "").replace(/\s/g, "")}`} className="text-white/70 hover:text-white block">
                            {c.support_phone}
                        </a>
                        <a href={`mailto:${c.support_email}`} className="text-white/70 hover:text-white block">
                            {c.support_email}
                        </a>
                    </div>
                    <div className="leading-relaxed">
                        <div className="text-white/80 font-semibold uppercase text-[10px] tracking-wider mb-1">Notice</div>
                        Doctor Digital Identity Platform provides professional identity and brand services only. It does not deliver medical diagnosis, treatment, or emergency services.
                    </div>
                </div>
            </div>
            <div className="border-t border-white/10">
                <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-start md:items-center justify-between text-[11px] text-white/50 gap-2">
                    <div>
                        © {year} <span className="text-white/70 font-semibold">{f.legal || "HUMANITYFIRST SOFTWARE TECHNOLOGY PRIVATE LIMITED. All rights reserved."}</span>
                    </div>
                    <div>
                        Security practices aligned with ISO 27001 and SOC 2 principles. We do not currently claim certification.
                    </div>
                </div>
            </div>
        </footer>
    );
}
